import { store } from '../store';
import { featureFlags } from '../config/featureFlags';
import { adminAuthenticatedFetch } from '../utils/adminAuthenticatedFetch';

const API_BASE = featureFlags.general.apiUrl;
const BASE = `${API_BASE}/api/public`;

export interface BannerRow {
  id?: number;
  side: 'LEFT' | 'RIGHT';
  sortOrder: number;
  visible: boolean;
  masked: boolean;
  badge?: string | null;
  title?: string | null;
  subtitle?: string | null;
  imageUrl?: string | null;
  linkUrl?: string | null;
  linkLabel?: string | null;
  bgColor?: string | null;
  widthPx?: number | null;
  heightPx?: number | null;
}

function withNoCache(url: string): string {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}_ts=${Date.now()}`;
}

/** Read the admin JWT from the Redux store — same pattern used by adminAuthService */
function getAdminToken(): string {
  return store.getState().auth.token || '';
}

export interface LandingConfig {
  published: boolean;
  /** When true, landing page hides hero, live signals, features, proof blocks, bottom CTA, and footer (nav stays). */
  maskAllSections: boolean;
  /** When true (default), public /market shows the blue public-view + Refresh strip above the hero. */
  showPublicRefreshBanner: boolean;
  heroTitle: string;
  heroSubtitle: string;
  heroCtaPrimary: string;
  heroCtaSecondary: string;
  heroCtaPrimaryUrl: string;
  heroCtaSecondaryUrl: string;
  showCtaPrimary: boolean;
  showCtaSecondary: boolean;
  navBrandText: string;
  navTagline: string;
  navBtn1Label: string;
  navBtn1Url: string;
  navBtn1Visible: boolean;
  navBtn2Label: string;
  navBtn2Url: string;
  navBtn2Visible: boolean;
  navBtn3Label: string;
  navBtn3Url: string;
  navBtn3Visible: boolean;
  showOracleFeed: boolean;
  showBtcFilter: boolean;
  maskBtcRegime: boolean;
  showMicroReversalFilter: boolean;
  maskMicroReversal: boolean;
  showParticipation: boolean;
  maskParticipation: boolean;
  showAiRegime: boolean;
  maskAiRegime: boolean;
  showMajorCoinsMarket: boolean;
  maskMajorCoinsMarket: boolean;
  showMajorCoinsChartStyleOptions: boolean;
  majorCoinsDefaultChart: string;
  showSectorBreakdown: boolean;
  showSignalCardOracle: boolean;
  signalCardOracleIcon: string;
  signalCardOracleTitle: string;
  signalCardOracleDesc: string;
  showSignalCardBtcRegime: boolean;
  signalCardBtcRegimeIcon: string;
  signalCardBtcRegimeTitle: string;
  signalCardBtcRegimeDesc: string;
  showSignalCardMicroReversal: boolean;
  signalCardMicroReversalIcon: string;
  signalCardMicroReversalTitle: string;
  signalCardMicroReversalDesc: string;
  showSignalCardParticipation: boolean;
  signalCardParticipationIcon: string;
  signalCardParticipationTitle: string;
  signalCardParticipationDesc: string;
  showSignalCardRisk: boolean;
  signalCardRiskIcon: string;
  signalCardRiskTitle: string;
  signalCardRiskDesc: string;
  oracleFeedLimit: number;
  announcement: string | null;
  // Footer
  footerCopyright: string;
  footerLink1Label: string;
  footerLink1Url: string;
  footerLink1Visible: boolean;
  footerLink2Label: string;
  footerLink2Url: string;
  footerLink2Visible: boolean;
  footerLink3Label: string;
  footerLink3Url: string;
  footerLink3Visible: boolean;
  footerLink4Label: string;
  footerLink4Url: string;
  footerLink4Visible: boolean;
  // Bottom CTA banner (independent of hero section)
  ctaBannerTitle: string;
  ctaBannerSubtitle: string;
  // ── Proof sections ──────────────────────────────────────────────────────────
  // 1. Market condition accuracy
  showProofRegime: boolean;
  proofRegimeTitle: string;
  proofRegimeSubtitle: string;
  proofRegimeAccuracy: string;        // e.g. "71%"
  proofRegimeRows: string;            // JSON-stringified array of {date,regime,outcome}
  // 2. Participation filter effectiveness
  showProofParticipation: boolean;
  proofParticipationTitle: string;
  proofParticipationSubtitle: string;
  proofHotAvg: string;                // e.g. "+12%"
  proofCoolAvg: string;               // e.g. "+2%"
  proofParticipationRows: string;     // JSON-stringified array of {label,result}
  // 3. Signal quality / bias accuracy
  showProofSignals: boolean;
  proofSignalsTitle: string;
  proofSignalsSubtitle: string;
  proofSignalWinRate: string;         // e.g. "68%"
  proofSignalAvgMove: string;         // e.g. "+8.4%"
  proofSignalMaxDD: string;           // e.g. "-3.1%"
  proofSignalRows: string;            // JSON-stringified array of {coin,bias,window,outcome}
  // 4. Winning trades (dynamic rows from active bots)
  showProofWinningTrades: boolean;
  proofWinningTradesTitle: string;
  proofWinningTradesSubtitle: string;
  showProofWinningTradesStrategyName: boolean;
  showProofLosingTrades: boolean;
  showProofWinningTradesDailyPnl: boolean;
  showProofWinningTradesDailyPnlPerStrategy: boolean;
  maskWinningTrades: boolean;
  winningTradesDisplayLimit: number;
  showAboutSection: boolean;
  maskAboutSection: boolean;
}

/** Safe defaults when /landing/config is unreachable — keeps public landing visible (P3). */
export const FALLBACK_LANDING_CONFIG: Partial<LandingConfig> & { published: true } = {
  published: true,
  maskAllSections: false,
  showPublicRefreshBanner: true,
  heroTitle: 'Know When the Market is Alive',
  heroSubtitle:
    'Real-time trading signals. Oracle rankings. BTC regime detection. Trade smarter on any platform.',
  heroCtaPrimary: 'Open Trading Account',
  heroCtaSecondary: 'Sign In',
  heroCtaPrimaryUrl: '/register',
  heroCtaSecondaryUrl: '/login',
  showCtaPrimary: true,
  showCtaSecondary: true,
  maskWinningTrades: true,
  maskBtcRegime: false,
  maskMicroReversal: false,
  maskParticipation: false,
  showBtcFilter: true,
  showMicroReversalFilter: false,
  showParticipation: true,
  showOracleFeed: true,
  showAiRegime: true,
  maskAiRegime: false,
  showMajorCoinsMarket: true,
  maskMajorCoinsMarket: false,
  showMajorCoinsChartStyleOptions: true,
  majorCoinsDefaultChart: 'donut',
  showSectorBreakdown: false,
  showAboutSection: false,
  maskAboutSection: false,
};

export interface MajorCoinMarketRow {
  symbol: string;
  name: string;
  price: number;
  change24h: number;
  marketCap: number;
  sharePercent: number;
}

export interface MajorCoinsMarketSnapshot {
  coins: MajorCoinMarketRow[];
  totalMarketCap: number;
  updatedAt: number;
  source: 'coingecko' | 'kucoin_estimate' | 'unavailable' | string;
}

export interface BtcRegime {
  regime: 'BULLISH' | 'BEARISH' | 'RANGING' | 'UNKNOWN';
  description: string;
  colour: 'green' | 'red' | 'amber' | 'grey';
  price: number;
  change24h: number;
  updatedAt: number;
}

export interface MicroReversalSnapshot {
  blocked: boolean;
  status: 'CLEAR' | 'BLOCKED' | 'UNKNOWN';
  description: string;
  colour: 'green' | 'red' | 'amber' | 'grey';
  dropFromHighPercent: number;
  localHigh: number;
  currentPrice: number;
  minDropPercent: number;
  maxDropPercent: number;
  lookbackCandles: number;
  updatedAt: number;
  available: boolean;
}

export interface CoinActivity {
  symbol: string;
  /** Human-readable name, e.g. "Micron Technology Inc." or "Bitcoin" */
  displayName?: string | null;
  /** e.g. "NASDAQ Stock Perpetual" or "Crypto Perpetual" */
  assetTypeLabel?: string | null;
  marketType?: string | null;
  /** e.g. "Stock Index", "Majors", "Meme" */
  sectorLabel?: string | null;
  /** KuCoin futures ticker, e.g. MUUSDTM */
  futuresSymbol?: string | null;
  price?: number;
  change24h: number;
  volume24h: number;
  turnover24h?: number;
  active: boolean;
  sector?: string | null;
}

export interface MarketCoinPositionRow {
  strategyId: number | null;
  hedgePositionId?: number | null;
  strategyName: string;
  symbol: string;
  side: string;
  amount: number;
  entryPrice: number;
  currentPrice: number;
  unrealizedPnl: number;
  status: string;
  createdAt?: string | null;
}

export interface MarketCoinPositionsPayload {
  symbol: string;
  totalOpen: number;
  positions: MarketCoinPositionRow[];
}

export interface MarketOpenPositionResult {
  success: boolean;
  message: string;
  strategyId?: number;
  strategyName?: string;
  symbol?: string;
  side?: string;
  positionId?: string | null;
  hedgePositionId?: number | null;
  entryPrice?: number;
  size?: number;
}

export interface MarketEligibleStrategy {
  strategyId: number;
  strategyName: string;
  kucoinAccountName: string;
  kucoinConfigId?: number | null;
  marketType: string;
  fixedOrderAmount: number;
  leverage: number;
  hedgeMode: boolean;
  paperTrading: boolean;
}

export interface MarketKucoinAccountOption {
  id: number;
  name: string;
  shared: boolean;
  selectable: boolean;
}

export interface MarketCoinOpenOptionsPayload {
  strategies: MarketEligibleStrategy[];
  directAccounts?: MarketKucoinAccountOption[];
  defaultDirectAmount?: number;
  defaultDirectLeverage?: number;
}

export type MarketOpenMode = 'STRATEGY' | 'DIRECT';

export interface MarketOpenPositionPayload {
  symbol: string;
  side: string;
  openMode?: MarketOpenMode;
  strategyId?: number;
  kucoinConfigId?: number;
  amount?: number;
  leverage?: number;
}

export interface MarketClosePositionPayload {
  symbol: string;
  hedgePositionId: number;
}

export interface SectorActivityGroup {
  id: string;
  label: string;
  activeCoins: number;
  totalCoins: number;
  rate: number;
  coins: CoinActivity[];
}

export interface SectorBreakdownSnapshot {
  sectors: SectorActivityGroup[];
  updatedAt: number;
  available: boolean;
}

export interface ParticipationSnapshot {
  level: 'HIGH' | 'MODERATE' | 'LOW' | 'UNKNOWN';
  description: string;
  rate: number;
  activeCoins: number;
  totalCoins: number;
  coins: CoinActivity[];
  updatedAt: number;
}

export interface OracleCoinRank {
  symbol: string;
  signal: 'LONG' | 'SHORT' | 'WATCH' | 'NEUTRAL';
  score: number;
  reasons: string[];
  rank: number;
  tier: 'oracle-top' | 'fallback' | 'neutral';
}

export interface OracleFeedSnapshot {
  oracleActive: boolean;
  confidence: number;
  coins: OracleCoinRank[];
  updatedAt: number;
}

export interface WinningTradeRow {
  symbol: string;
  side: 'LONG' | 'SHORT' | 'CLOSE' | string;
  entryPrice: number;
  exitPrice: number;
  pnl: number;
  exitReason?: string | null;
  closedAt?: string | null;
  strategyName?: string | null;
  dataSource?: 'KUCOIN_LIVE' | 'DB_ROLLBACK' | string;
  /** UTC-day realized PnL sum across active-bot closes on the trade's close day. */
  dailyPnl?: number;
  /** UTC-day realized PnL sum for this strategy only on the trade's close day. */
  dailyPnlPerStrategy?: number;
}

export interface MarketSnapshot {
  published: boolean;
  btcRegime?: BtcRegime;
  microReversal?: MicroReversalSnapshot;
  signalsBlockedByMicroReversal?: boolean;
  participation?: ParticipationSnapshot;
  sectorBreakdown?: SectorBreakdownSnapshot;
  futuresActivity?: SectorActivityGroup;
  majorCoinsMarket?: MajorCoinsMarketSnapshot;
  oracleFeed?: OracleFeedSnapshot;
  oracleFeedStockIndex?: OracleFeedSnapshot;
  winningTrades?: WinningTradeRow[];
}

async function get<T>(path: string, timeoutMs = 10000): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(path, { signal: controller.signal });
    if (!res.ok) throw new Error(`API error ${res.status}`);
    return res.json();
  } finally {
    clearTimeout(timer);
  }
}

export interface CoinPricePoint {
  time: number;
  price: number;
  label: string;
}

export interface FaqPayload {
  published: boolean;
  maskAll: boolean;
  pageTitle: string;
  pageSubtitle: string;
  items?: { id: number; question: string; answer: string }[];
}

export const publicFaqService = {
  getFaq: () => get<FaqPayload>(`${BASE}/faq`),
};

export const publicMarketService = {
  getLandingConfig: () => get<LandingConfig>(`${BASE}/landing/config`),
  getBtcRegime: () => get<BtcRegime>(withNoCache(`${BASE}/market/btc-regime`)),
  getParticipation: (limit = 12) => get<ParticipationSnapshot>(withNoCache(`${BASE}/market/participation?limit=${limit}`)),
  getSectorBreakdown: () => get<SectorBreakdownSnapshot>(withNoCache(`${BASE}/market/sector-breakdown`)),
  getCoinProfile: (symbol: string) =>
    get<CoinActivity>(withNoCache(`${BASE}/market/coin-profile?symbol=${encodeURIComponent(symbol)}`)),
  getOracleFeed: (limit = 10, universe: 'MEME' | 'STOCK_INDEX' = 'MEME') =>
    get<OracleFeedSnapshot>(
      withNoCache(`${BASE}/market/oracle-feed?limit=${limit}&universe=${encodeURIComponent(universe)}`)
    ),
  getFullSnapshot: (oracleLimit = 10, participationLimit = 12) =>
    get<MarketSnapshot>(
      withNoCache(`${BASE}/market/snapshot?oracleLimit=${oracleLimit}&participationLimit=${participationLimit}`),
      20000
    ),
  /** JWT or session cookie; requires VIEW_MARKET_SIGNALS on the retail user */
  getRetailLandingState: (oracleLimit = 10, participationLimit = 12) =>
    getJsonAuthedRetail<MarketSnapshot & LandingConfig & { signalViewer?: boolean }>(
      withNoCache(`${API_BASE}/api/retail/market/landing-state?oracleLimit=${oracleLimit}&participationLimit=${participationLimit}`)
    ),
  /** Admin JWT; requires VIEW_MARKET_SIGNALS or landing-page permissions (see backend) */
  getAdminMarketLandingState: async (oracleLimit = 10, participationLimit = 12) => {
    const res = await adminFetch(
      withNoCache(`${API_BASE}/api/admin/market/landing-state?oracleLimit=${oracleLimit}&participationLimit=${participationLimit}`),
      { headers: { Authorization: `Bearer ${getAdminToken()}` } },
      20000
    );
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json() as Promise<MarketSnapshot & LandingConfig & { signalViewer?: boolean }>;
  },
  /** Privileged retail: open bot positions for a base symbol (market signals drawer) */
  getRetailCoinPositions: (symbol: string) =>
    getJsonAuthedRetail<MarketCoinPositionsPayload>(
      withNoCache(`${API_BASE}/api/retail/market/coin-positions?symbol=${encodeURIComponent(symbol)}`)
    ),
  getRetailAllCoinPositions: () =>
    getJsonAuthedRetail<MarketCoinPositionsPayload>(
      withNoCache(`${API_BASE}/api/retail/market/coin-positions?scope=all`)
    ),
  /** Privileged admin: open bot positions for a base symbol (market signals drawer) */
  getAdminCoinPositions: async (symbol: string) => {
    const res = await adminFetch(
      withNoCache(`${API_BASE}/api/admin/market/coin-positions?symbol=${encodeURIComponent(symbol)}`),
      { headers: { Authorization: `Bearer ${getAdminToken()}` } },
      15000
    );
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json() as Promise<MarketCoinPositionsPayload>;
  },
  getAdminAllCoinPositions: async () => {
    const res = await adminFetch(
      withNoCache(`${API_BASE}/api/admin/market/coin-positions?scope=all`),
      { headers: { Authorization: `Bearer ${getAdminToken()}` } },
      15000
    );
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json() as Promise<MarketCoinPositionsPayload>;
  },
  getRetailCoinChart: (symbol: string, hours = 24) =>
    getJsonAuthedRetail<CoinPricePoint[]>(
      withNoCache(
        `${API_BASE}/api/retail/market/coin-chart?symbol=${encodeURIComponent(symbol)}&hours=${hours}`
      ),
      20000
    ),
  getAdminCoinChart: async (symbol: string, hours = 24) => {
    const res = await adminFetch(
      withNoCache(
        `${API_BASE}/api/admin/market/coin-chart?symbol=${encodeURIComponent(symbol)}&hours=${hours}`
      ),
      { headers: { Authorization: `Bearer ${getAdminToken()}` } },
      20000
    );
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json() as Promise<CoinPricePoint[]>;
  },
  getRetailCoinOpenOptions: (symbol: string) =>
    getJsonAuthedRetail<MarketEligibleStrategy[]>(
      withNoCache(`${API_BASE}/api/retail/market/coin-open-options?symbol=${encodeURIComponent(symbol)}`)
    ),
  getAdminCoinOpenOptions: async (symbol: string) => {
    const res = await adminFetch(
      withNoCache(`${API_BASE}/api/admin/market/coin-open-options?symbol=${encodeURIComponent(symbol)}`),
      { headers: { Authorization: `Bearer ${getAdminToken()}` } },
      15000
    );
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json() as Promise<MarketCoinOpenOptionsPayload>;
  },
  openRetailCoinPosition: (payload: MarketOpenPositionPayload & { strategyId: number }) =>
    postJsonAuthedRetail<MarketOpenPositionResult>(
      `${API_BASE}/api/retail/market/coin-positions/open`,
      payload,
      45000
    ),
  openAdminCoinPosition: async (payload: MarketOpenPositionPayload) => {
    const res = await adminFetch(
      `${API_BASE}/api/admin/market/coin-positions/open`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify(payload),
      },
      45000
    );
    const json = await res.json() as MarketOpenPositionResult;
    if (!res.ok) {
      const err = new Error(json?.message || `${res.status}`) as Error & { body?: MarketOpenPositionResult };
      err.body = json;
      throw err;
    }
    return json;
  },
  closeAdminCoinPosition: async (payload: MarketClosePositionPayload) => {
    const res = await adminFetch(
      `${API_BASE}/api/admin/market/coin-positions/close`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify(payload),
      },
      45000
    );
    const json = await res.json() as MarketOpenPositionResult;
    if (!res.ok) {
      const err = new Error(json?.message || `${res.status}`) as Error & { body?: MarketOpenPositionResult };
      err.body = json;
      throw err;
    }
    return json;
  },
};

const ADMIN_LANDING = `${API_BASE}/api/admin/landing`;

function retailAuthInit(): Pick<RequestInit, 'headers' | 'credentials'> {
  const { token, type } = store.getState().auth;
  const headers: Record<string, string> = {};
  if (token && type === 'JWT') {
    headers.Authorization = `Bearer ${token}`;
  }
  return {
    headers,
    credentials: type === 'SESSION' ? 'include' : 'omit',
  };
}

async function getJsonAuthedRetail<T>(path: string, timeoutMs = 20000): Promise<T> {
  const { headers, credentials } = retailAuthInit();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(path, { signal: controller.signal, headers, credentials });
    if (!res.ok) throw new Error(`API error ${res.status}`);
    return res.json();
  } finally {
    clearTimeout(timer);
  }
}

async function postJsonAuthedRetail<T>(path: string, body: unknown, timeoutMs = 30000): Promise<T> {
  const { headers, credentials } = retailAuthInit();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(path, {
      method: 'POST',
      signal: controller.signal,
      headers: { ...headers, 'Content-Type': 'application/json' },
      credentials,
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) {
      const err = new Error((json as any)?.message || `API error ${res.status}`) as Error & { body?: T; status?: number };
      err.body = json;
      err.status = res.status;
      throw err;
    }
    return json;
  } finally {
    clearTimeout(timer);
  }
}

function adminFetch(url: string, init: RequestInit = {}, timeoutMs = 8000): Promise<Response> {
  return adminAuthenticatedFetch(url, init, timeoutMs);
}

// Admin endpoints — token is read automatically from Redux store
export const landingAdminService = {
  getConfig: async () => {
    const res = await adminFetch(`${ADMIN_LANDING}/config`, {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },
  updateConfig: async (data: Partial<LandingConfig> & { announcement?: string }) => {
    const res = await adminFetch(`${ADMIN_LANDING}/config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken()}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },
  publish: async () => {
    const res = await adminFetch(`${ADMIN_LANDING}/publish`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },
  unpublish: async () => {
    const res = await adminFetch(`${ADMIN_LANDING}/unpublish`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },
  // ── Dynamic banners ──────────────────────────────────────────────────────
  listBanners: async (): Promise<BannerRow[]> => {
    const res = await adminFetch(`${ADMIN_LANDING}/banners`, {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },
  createBanner: async (data: Partial<BannerRow>): Promise<BannerRow> => {
    const res = await adminFetch(`${ADMIN_LANDING}/banners`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken()}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },
  updateBanner: async (id: number, data: Partial<BannerRow>): Promise<BannerRow> => {
    const res = await adminFetch(`${ADMIN_LANDING}/banners/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken()}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },
  deleteBanner: async (id: number): Promise<void> => {
    const res = await adminFetch(`${ADMIN_LANDING}/banners/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
  },
  /** Upload a banner image file; returns the hosted URL string. */
  uploadBannerImage: async (file: File): Promise<string> => {
    const form = new FormData();
    form.append('file', file);
    const res = await adminFetch(`${ADMIN_LANDING}/banners/upload-image`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
      body: form,
    }, 30000);
    if (!res.ok) throw new Error(`${res.status}`);
    const json = await res.json();
    // Backend may return { url: '...' } or { data: { url: '...' } }
    const url: string = json?.url ?? json?.data?.url ?? json?.imageUrl ?? json?.data?.imageUrl ?? '';
    if (!url) throw new Error('No URL in upload response');
    return url;
  },
};

const ADMIN_FAQ = `${API_BASE}/api/admin/faq`;

export const faqAdminService = {
  getConfig: async () => {
    const res = await adminFetch(`${ADMIN_FAQ}/config`, { headers: { Authorization: `Bearer ${getAdminToken()}` } });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },
  updateConfig: async (data: { published?: boolean; maskAll?: boolean; pageTitle?: string; pageSubtitle?: string }) => {
    const res = await adminFetch(`${ADMIN_FAQ}/config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken()}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },
  publish: async () => {
    const res = await adminFetch(`${ADMIN_FAQ}/publish`, { method: 'POST', headers: { Authorization: `Bearer ${getAdminToken()}` } });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },
  unpublish: async () => {
    const res = await adminFetch(`${ADMIN_FAQ}/unpublish`, { method: 'POST', headers: { Authorization: `Bearer ${getAdminToken()}` } });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },
  createItem: async (question: string, answer: string) => {
    const res = await adminFetch(`${ADMIN_FAQ}/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken()}` },
      body: JSON.stringify({ question, answer }),
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },
  updateItem: async (id: number, data: { question?: string; answer?: string; visible?: boolean }) => {
    const res = await adminFetch(`${ADMIN_FAQ}/items/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken()}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },
  deleteItem: async (id: number) => {
    const res = await adminFetch(`${ADMIN_FAQ}/items/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getAdminToken()}` } });
    if (!res.ok) throw new Error(`${res.status}`);
  },
  reorderItems: async (orders: { id: number; displayOrder: number }[]) => {
    const res = await adminFetch(`${ADMIN_FAQ}/items/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken()}` },
      body: JSON.stringify(orders),
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json();
  },
};
